import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import HoverIcon from 'nav/expanded/HoverIcon';
import Popover from 'ui-library/floating-ui/Popover';
import AddIcon from 'ui-library/icons/AddIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Cell, Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import * as modals from 'util/Modals';
import StageEntryTemplateLink from 'workflows/entry-templates/StageEntryTemplateLink';
import WorkflowEntryTemplateModal from 'workflows/entry-templates/WorkflowEntryTemplateModal';
import {
  ExperimentalConditionPopoverContent,
  experimentalConditionValuesToString,
} from 'workflows/util/ExperimentalConditionUtil';
import * as WorkflowTemplateActions from 'workflows/WorkflowTemplateActions';

import {STAGE_CONFIGURATION_FULL_SHAPE, StageConfiguration} from '../WorkflowConstants';

interface StageConfigurationTableProps {
  isEditable: boolean;
  onCreateStageConfiguration: () => void;
  onEditStageConfiguration: (stageConfiguration: StageConfiguration) => void;
  stageConfigurations: Array<StageConfiguration>;
}

interface State {
  editingStageEntryTemplate: StageConfiguration | null;
}

export default class StageConfigurationTable extends React.Component<StageConfigurationTableProps, State> {
  static propTypes = {
    isEditable: PropTypes.bool.isRequired,
    onCreateStageConfiguration: PropTypes.func,
    onEditStageConfiguration: PropTypes.func,
    stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_FULL_SHAPE.isRequired).isRequired,
  };

  state: State = {
    // If editingStageEntryTemplate is not null WorkflowEntryTemplateModal is open for editing the template
    editingStageEntryTemplate: null,
  };

  UNSAFE_componentWillReceiveProps(nextProps: StageConfigurationTableProps): void {
    // This component passes information from this.props.stageConfigurations to
    // this.state.editingStageEntryTemplate to the WorkflowEntryTemplateModal. In case the incoming prop
    // changes (this happens when the user changes the tags or tagSchema while editing), update this.state.
    const {editingStageEntryTemplate} = this.state;
    if (!editingStageEntryTemplate) {
      return;
    }
    const newStageConf = nextProps.stageConfigurations.find((sc) => sc.id === editingStageEntryTemplate.id);
    if (!newStageConf) {
      return;
    }
    this.setState({
      editingStageEntryTemplate: {
        ...editingStageEntryTemplate,
        tags: newStageConf.tags,
        tagSchema: newStageConf.tagSchema,
      },
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleStageEntryTemplateClicked = (stageEntryTemplate: StageConfiguration) => {
    this.setState({editingStageEntryTemplate: stageEntryTemplate});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCloseStageEntryTemplateModal = () => {
    this.setState({editingStageEntryTemplate: null});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleDeleteStageConfiguration = (stageConfiguration: StageConfiguration) => {
    modals
      .confirm(
        'Confirm Delete Stage',
        `Are you sure you want to delete the stage ${stageConfiguration.stageName}?` +
          ` This action cannot be undone.`,
        'danger'
      )
      .done(() => {
        WorkflowTemplateActions.deleteStageConfiguration(stageConfiguration.id);
      });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleReorderStageConfiguration = (stageConfigurationId: string, newIndex: number) => {
    WorkflowTemplateActions.reorderStageConfiguration(stageConfigurationId, newIndex);
  };

  _getHeaderCells(): Array<Cell> {
    return [
      {
        key: 'stage',
        contents: (
          <div className="u-vertical-center-items">
            <span>Stage</span>
            {this.props.isEditable && <AddIcon onClick={this.props.onCreateStageConfiguration} />}
          </div>
        ),
      },
      {
        key: 'template',
        contents: 'Entry Template',
      },
      {
        key: 'conditions',
        contents: 'Experimental Conditions',
      },
      {
        key: 'hover icons',
        contents: '',
      },
    ];
  }

  _getRows(): Array<Row> {
    return this.props.stageConfigurations.map((stageConfiguration) => {
      // eslint-disable-next-line no-restricted-syntax
      const experimentalConditionValues = _.flatten(
        stageConfiguration.experimentalConditions.map((ec) => ec.values)
      );
      const hoverIcons = this.props.isEditable && (
        <div className="stageConfigurationTable-hoverIcons">
          <HoverIcon
            key={`edit-${stageConfiguration.id}`}
            icon="edit"
            onClick={this.props.onEditStageConfiguration}
            result={stageConfiguration}
          />
          <HoverIcon
            key={`delete-${stageConfiguration.id}`}
            icon="delete"
            onClick={this._handleDeleteStageConfiguration}
            result={stageConfiguration}
          />
        </div>
      );

      return {
        id: stageConfiguration.id,
        cells: [
          {
            key: 'name',
            contents: stageConfiguration.stageName,
          },
          {
            key: 'template',
            contents: (
              <StageEntryTemplateLink
                stageConfiguration={stageConfiguration}
                onClick={() => {
                  this._handleStageEntryTemplateClicked(stageConfiguration);
                }}
              />
            ),
          },
          {
            key: 'conditions',
            contents: experimentalConditionValues.length ? (
              <Popover
                renderContent={() => (
                  <ExperimentalConditionPopoverContent
                    experimentalConditions={stageConfiguration.experimentalConditions}
                  />
                )}
              >
                <span>{experimentalConditionValuesToString(experimentalConditionValues)}</span>
              </Popover>
            ) : (
              <span className="u-faded">None</span>
            ),
          },
          {
            key: 'hover icons',
            contentsClassName: 'u-justify-content-end',
            contents: hoverIcons,
          },
        ],
      };
    });
  }

  render(): JSX.Element {
    return (
      <div>
        {this.state.editingStageEntryTemplate && (
          <ModalAnchor open={true}>
            <WorkflowEntryTemplateModal
              entryTemplate={this.state.editingStageEntryTemplate}
              fileType={ModelTypeConstants.STAGE_CONFIGURATION}
              isReadonly={!this.props.isEditable}
              onClose={this._handleCloseStageEntryTemplateModal}
            />
          </ModalAnchor>
        )}
        <ReorderableSimpleTable
          headerCells={this._getHeaderCells()}
          isDraggingDisabled={!this.props.isEditable}
          noRowsDisplay="No stages"
          onReorderRow={this._handleReorderStageConfiguration}
          rows={this._getRows()}
        />
      </div>
    );
  }
}
